import { Play, XCircle, RotateCcw, ChevronLeft } from 'lucide-react';
import { JobState, OptimizeSettings } from './types';
import { cn } from '@/lib/utils';

interface Props {
  jobState: JobState;
  selectedCount: number;
  totalCount: number;
  selectedSizeMb: number;
  settings: OptimizeSettings;
  onSettingsChange: (patch: Partial<OptimizeSettings>) => void;
  onOptimizeAll: () => void;
  onOptimizeSelected: () => void;
  onCancel: () => void;
  onRunAgain: () => void;
  onBack: () => void;
}

export default function BottomActionBar({
  jobState, selectedCount, totalCount, selectedSizeMb,
  settings, onSettingsChange,
  onOptimizeAll, onOptimizeSelected, onCancel, onRunAgain, onBack,
}: Props) {
  const { status, filesOptimized, filesCount, savedSize } = jobState;
  const isRunning  = jobState.active || status === 'running' || status === 'starting';
  const isDone     = status === 'success';
  const isFailed   = status === 'failed' || status === 'cancelled';
  const isIdle     = status === 'idle' || isFailed;

  function formatSize(mb: number) {
    if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
    return `${mb.toFixed(0)} MB`;
  }

  return (
    <div
      className="sticky bottom-0 flex items-center gap-4 px-6 py-3 border-t shrink-0"
      style={{
        background: 'rgba(7,9,16,0.97)',
        borderColor: isRunning ? 'rgba(0,255,209,0.12)' : isDone ? 'rgba(0,255,102,0.1)' : 'rgba(255,255,255,0.04)',
        backdropFilter: 'blur(12px)',
        zIndex: 40,
      }}
    >
      {isIdle && (
        <>
          <div className="text-[10px] text-slate-500 shrink-0">
            {selectedCount > 0
              ? <><span className="text-slate-200 font-bold">{selectedCount} files selected</span> · {formatSize(selectedSizeMb)}</>
              : <span className="text-slate-600">No files selected — will optimize all</span>
            }
          </div>
          <div className="flex items-center gap-4 ml-auto">
            <div className="flex items-center gap-1.5 text-[9px] text-slate-500">
              <span>Limit</span>
              <input
                type="number"
                value={settings.sizeThreshold}
                onChange={e => onSettingsChange({ sizeThreshold: e.target.value })}
                className="w-14 bg-black/60 border border-white/[0.08] rounded px-1.5 py-0.5 text-[9px] text-white focus:outline-none focus:border-[#00ffd1]"
              />
            </div>
            <label className="flex items-center gap-1 text-[9px] text-slate-500 cursor-pointer">
              <input type="checkbox" checked={settings.downsize} onChange={e => onSettingsChange({ downsize: e.target.checked })} className="w-3 h-3 rounded accent-[#00ffd1]" />
              Downsize
            </label>
            <label className="flex items-center gap-1 text-[9px] text-slate-500 cursor-pointer">
              <input type="checkbox" checked={settings.format} onChange={e => onSettingsChange({ format: e.target.checked })} className="w-3 h-3 rounded accent-[#00ffd1]" />
              Format
            </label>
            <button
              onClick={onOptimizeAll}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/[0.08] bg-white/[0.02] text-[10px] font-bold uppercase tracking-wide text-slate-400 hover:bg-white/[0.05] transition-all"
            >
              <Play size={10} fill="currentColor" />
              Optimize All
            </button>
            {selectedCount > 0 && (
              <button
                onClick={onOptimizeSelected}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wide text-[#0a0f1a] hover:opacity-90 transition-all"
                style={{ background: 'linear-gradient(90deg,#00ffd1,#a78bfa)' }}
              >
                <Play size={10} fill="currentColor" />
                Optimize · {selectedCount} {selectedCount === 1 ? 'File' : 'Files'}
              </button>
            )}
          </div>
        </>
      )}

      {isRunning && (
        <>
          <div className="text-[10px] text-slate-500">
            <span className="text-slate-200 font-bold">{filesOptimized.toLocaleString()} / {filesCount.toLocaleString()}</span>
            {savedSize > 0 && <> · {formatSize(savedSize)} saved</>}
          </div>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-600">
              Settings locked — optimization in progress
            </span>
            <button onClick={onCancel} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-500/30 bg-red-500/5 text-[10px] font-bold uppercase tracking-wide text-red-400 hover:bg-red-500/15 transition-all">
              <XCircle size={12} />
              Cancel
            </button>
          </div>
        </>
      )}

      {isDone && (
        <>
          <div className="flex items-center gap-2 text-[10px]">
            <span className="text-[#00ff66]">✓</span>
            <span className="text-slate-200 font-bold">{filesCount.toLocaleString()} files optimized</span>
            {savedSize > 0 && <span className="text-slate-500">· {formatSize(savedSize)} reclaimed</span>}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={onRunAgain} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/[0.08] bg-white/[0.02] text-[10px] font-semibold text-slate-400 hover:bg-white/[0.05] transition-all">
              <RotateCcw size={11} />
              Run Again
            </button>
            <button onClick={onBack} className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg border text-[10px] font-bold uppercase tracking-wide transition-all" style={{ color: '#00ffd1', borderColor: 'rgba(0,255,209,0.2)', background: 'rgba(0,255,209,0.06)' }}>
              <ChevronLeft size={12} />
              Back to Folders
            </button>
          </div>
        </>
      )}
    </div>
  );
}
